import * as d3 from 'd3';
import { buildSeparatedRadialTargets } from './personTreeGraph.mjs';
import { packLayoutRegionTargets } from './personTreeClusters.mjs';

export const PERSON_TREE_LAYOUT_VERSION = 2;

export function isCurrentPersonLayout(layout) {
    return layout?.version === PERSON_TREE_LAYOUT_VERSION;
}

/** Forces operate in independent local coordinate systems; cross-group edges only render. */
export function createLocalGroupForce(groups, links, spacing) {
    let forces = [];
    const groupByNodeId = new Map(groups.flatMap(group => group.nodes.map(node => [node.id, group.id])));
    const linksByGroup = new Map(groups.map(group => [group.id, []]));
    for (const link of links) {
        const source = link.source?.id || link.source;
        const target = link.target?.id || link.target;
        const groupId = groupByNodeId.get(source);
        if (groupId && groupId === groupByNodeId.get(target)) linksByGroup.get(groupId).push({ ...link });
    }
    const force = alpha => forces.forEach(item => item(alpha));
    force.initialize = (nodes, random = d3.randomLcg(0.42)) => {
        forces = [];
        for (const group of groups) {
            const localForces = [
                d3.forceLink(linksByGroup.get(group.id)).id(node => node.id)
                    .distance(spacing.linkDistance).strength(spacing.linkStrength),
                d3.forceManyBody().strength(spacing.charge),
                d3.forceCollide(spacing.collide),
                d3.forceX(0).strength(spacing.clusterPull),
                d3.forceY(0).strength(spacing.clusterPull),
            ];
            localForces.forEach(item => item.initialize(group.nodes, random));
            forces.push(...localForces);
        }
    };
    return force;
}

export function measureLocalGroup(group, padding = 40) {
    const nodes = group.nodes.filter(node => Number.isFinite(node.x) && Number.isFinite(node.y));
    if (!nodes.length) return { ...group, x: 0, y: 0, radius: padding };
    const x = (Math.min(...nodes.map(node => node.x)) + Math.max(...nodes.map(node => node.x))) / 2;
    const y = (Math.min(...nodes.map(node => node.y)) + Math.max(...nodes.map(node => node.y))) / 2;
    const labelRadius = (String(group.label || '').length * 7.5 + 28) / 2;
    const bodyRadius = group.encloseRectangle
        ? Math.hypot(Math.max(...nodes.map(node => Math.abs(node.x - x))), Math.max(...nodes.map(node => Math.abs(node.y - y))))
        : Math.max(...nodes.map(node => Math.hypot(node.x - x, node.y - y)));
    const radius = Math.max(labelRadius, bodyRadius + padding);
    return { ...group, x, y, radius };
}

/** Translate whole measured groups. No settling force runs after this placement. */
export function arrangeMeasuredGroups({ groups, subclades, regions, centerX, centerY, spreadFactor = 1 }) {
    const measured = groups.map(group => measureLocalGroup(group, group.padding || 40));
    const byId = new Map(measured.map(group => [group.id, group]));
    const core = byId.get('core');
    const radial = buildSeparatedRadialTargets({
        subclades: subclades.map(subclade => ({ ...subclade,
            measuredRadius: byId.get(`subclade:${subclade.id}`)?.radius || 0 })),
        innerRadius: core?.nodes.length ? core.radius : 0,
        padding: 48 * spreadFactor,
        minimumGroupRadius: 0,
    });
    const actualRegions = regions.map(region => ({ ...region,
        radius: region.type === 'main' ? Math.max(radial.outerRadius, core?.radius || 0)
            : byId.get(region.id)?.radius || region.radius }));
    const targets = packLayoutRegionTargets({
        regions: actualRegions, centerX, centerY,
        mainRadius: actualRegions.find(region => region.type === 'main')?.radius || 0,
        spreadFactor, targetAngles: new Map(subclades.map(subclade => [subclade.id, subclade.angle])),
    });
    for (const group of measured) {
        const offset = group.subcladeId ? radial.targets.get(group.subcladeId) : null;
        const target = group.id === 'core' || offset
            ? { x: centerX + (offset?.dx || 0), y: centerY + (offset?.dy || 0) }
            : targets.get(group.id);
        if (!target) continue;
        const dx = target.x - group.x;
        const dy = target.y - group.y;
        group.nodes.forEach(node => { node.x += dx; node.y += dy; node.vx = 0; node.vy = 0; });
        group.x = target.x;
        group.y = target.y;
    }
    return { groups: measured, regions: actualRegions.map(region => ({ ...region, ...targets.get(region.id) })) };
}
